// X3DH initial key agreement for Nexus E2E (Signal Extended Triple DH).
// Establishes a shared secret with an OFFLINE recipient from their published
// prekey bundle (КР-3), verifying the signed prekey first (КР-9).
//
// Identity = X25519 DH key + Ed25519 signing key. Signed prekey and one-time
// prekeys are X25519. Private keys never leave the device (КР-8).

import { generateDHKeyPair, generateSigningKeyPair, dh, sign, verify, kdf } from './asym.js';

const F = new Uint8Array(32).fill(0xff); // domain separator (Signal X3DH)
const concat = (...arrs) => {
  let n = 0; for (const a of arrs) n += a.length;
  const out = new Uint8Array(n); let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
};

export function generateIdentity() {
  return { dh: generateDHKeyPair(), sign: generateSigningKeyPair() };
}

export function generatePrekeys(identity, numOneTime = 20) {
  const spk = generateDHKeyPair();
  const spkSig = sign(identity.sign.priv, spk.pub);
  const opks = [];
  for (let i = 0; i < numOneTime; i++) opks.push({ id: i, ...generateDHKeyPair() });
  return { spk, spkSig, opks };
}

// Public bundle to publish (e.g. in me.public). The advertised one-time prekey is
// the pool's CURRENT one (`prekeys.currentOpkId`, set/rotated by the manager); the
// legacy `opkIndex` path is kept for the pure-function tests that don't rotate.
export function publicBundle(identity, prekeys, opkIndex = 0) {
  const opk = prekeys.currentOpkId != null
    ? prekeys.opks.find((o) => o.id === prekeys.currentOpkId)
    : prekeys.opks[opkIndex];
  return {
    idDH: identity.dh.pub,
    idSign: identity.sign.pub,
    spk: prekeys.spk.pub,
    spkSig: prekeys.spkSig,
    opk: opk ? { id: opk.id, pub: opk.pub } : null,
  };
}

function deriveSK(dhList) {
  return kdf(concat(F, ...dhList), new Uint8Array(32), 'NexusX3DH', 32);
}

// Initiator (Alice) → responder's bundle. Returns the shared secret, the
// associated data, the responder's initial ratchet key, and the `init` block
// the responder needs to derive the same secret.
export function initiatorX3DH(myIdentity, theirBundle) {
  if (!verify(theirBundle.idSign, theirBundle.spk, theirBundle.spkSig)) {
    throw new Error('E2E: signed prekey signature invalid'); // КР-9 / ТС-3
  }
  const ek = generateDHKeyPair();
  const parts = [
    dh(myIdentity.dh.priv, theirBundle.spk), // DH1 = DH(IK_A, SPK_B)
    dh(ek.priv, theirBundle.idDH),           // DH2 = DH(EK_A, IK_B)
    dh(ek.priv, theirBundle.spk),            // DH3 = DH(EK_A, SPK_B)
  ];
  // Use the one-time prekey ONLY when it carries a matchable id. The transport
  // (Tinode me.public) drops a 0-valued id, and without an id the responder can't
  // find the opk and skips DH4 — which would desync the shared secret. When the id
  // is absent both sides consistently fall back to DH1-3.
  const useOpk = theirBundle.opk && theirBundle.opk.id != null;
  if (useOpk) {
    parts.push(dh(ek.priv, theirBundle.opk.pub)); // DH4
  }
  return {
    sk: deriveSK(parts),
    ad: concat(myIdentity.dh.pub, theirBundle.idDH),
    ratchetPub: theirBundle.spk,
    init: { idDH: myIdentity.dh.pub, ek: ek.pub, opkId: useOpk ? theirBundle.opk.id : null },
  };
}

// Responder (Bob) derives the same secret from Alice's init + his own keys.
export function responderX3DH(myIdentity, myPrekeys, init) {
  const opk = init.opkId != null ? myPrekeys.opks.find((o) => o.id === init.opkId) : null;
  const parts = [
    dh(myPrekeys.spk.priv, init.idDH), // DH1
    dh(myIdentity.dh.priv, init.ek),   // DH2
    dh(myPrekeys.spk.priv, init.ek),   // DH3
  ];
  if (opk) parts.push(dh(opk.priv, init.ek)); // DH4
  return {
    sk: deriveSK(parts),
    ad: concat(init.idDH, myIdentity.dh.pub),
    ratchetKeyPair: myPrekeys.spk,
  };
}
